/**
 * e-Tesa — Dialog trimitere contract pe email.
 * - Atașează automat PDF-ul contractului (generat client-side din HTML)
 * - Checkbox pentru a include și devizul sursă (PDF generat client-side)
 * - Posibilitatea de încărcare fișiere arbitrare (anexe, facturi, fișe tehnice)
 * - Toate sosesc într-un singur email cu body profesionist.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Mail, Send, Loader2, AlertCircle, Receipt, ClipboardList
} from "lucide-react";
import { generateDevizPDF } from "@/utils/deviz/pdfGenerator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SendContractDialog({
  open,
  onOpenChange,
  contract,
  devizData,
  companyData,
  contractNumber,
  beneficiarName,
  beneficiarEmailHint,
  annexes = [],
  onSent,
}) {
  const [recipient, setRecipient] = useState(beneficiarEmailHint || "");
  const [recipientName, setRecipientName] = useState(beneficiarName || "");
  const [subject, setSubject] = useState(
    contractNumber ? `Contract ${contractNumber}` : "Contract"
  );
  const [message, setMessage] = useState("");
  const [includeDeviz, setIncludeDeviz] = useState(true);
  const [selectedAnnexIds, setSelectedAnnexIds] = useState([]);
  const [sending, setSending] = useState(false);

  const toggleAnnex = (id) => {
    setSelectedAnnexIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const resetAndClose = () => {
    if (sending) return;
    onOpenChange(false);
  };

  // === TRIMITE PRIN PORTAL CLIENT ===
  // Nu atașează nimic în email — urcă documentele în portal (via GridFS)
  // și trimite email scurt cu link către portal.
  const handleSendViaPortal = async () => {
    if (!recipient.trim()) {
      toast.error("Introdu email-ul destinatarului");
      return;
    }
    setSending(true);
    const loadingId = toast.loading("Se procesează trimiterea prin portal...");
    try {
      // Generăm PDF-ul devizului (opțional) ca să-l urcăm în portal
      let devizPdf = null;
      if (includeDeviz && devizData) {
        toast.loading("Se generează PDF-ul devizului...", { id: loadingId });
        const brandColor = (() => {
          try {
            const cid = companyData?.id || devizData?.company_id;
            return cid ? (localStorage.getItem(`deviz_brand_color_${cid}`) || "") : "";
          } catch { return ""; }
        })();
        try {
          devizPdf = await generateDevizPDF({
            devizInfo: devizData,
            workItems: devizData.workItems || [],
            totals: devizData.totals || {},
            percentages: devizData.percentages || { tva: 19 },
            currencyCode: devizData.currencyCode || "RON",
            isEUR: !!devizData.isEUR,
            exchangeRate: devizData.exchangeRate || 1,
            includeMaterials: false,
            mode: "email",
            companyLogoUrl: companyData?.logo_url || "",
            companyName: companyData?.name || companyData?.company_name || "",
            brandColor,
            notify: () => {},
          });
        } catch (e) { devizPdf = null; }
      }

      toast.loading("Se procesează trimiterea prin portal...", { id: loadingId });
      const initRes = await axios.post(`${API}/contracts/${contract.id}/send-via-portal`, {
        recipient_email: recipient.trim(),
        recipient_name: recipientName.trim() || null,
        subject: subject.trim() || null,
        message: message.trim() || null,
        include_deviz: includeDeviz && !!devizPdf?.base64,
        deviz_pdf_base64: devizPdf?.base64 || null,
        deviz_filename: devizPdf?.filename || null,
        annex_ids: selectedAnnexIds,
      });
      const jobId = initRes?.data?.job_id;
      if (!jobId) {
        toast.error("Nu s-a putut iniţia trimiterea prin portal", { id: loadingId });
        return;
      }
      toast.loading("Se urcă documentele în portal...", { id: loadingId });
      let finalStatus = "processing";
      let finalError = null;
      let finalLog = null;
      for (let attempt = 0; attempt < 90; attempt++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const statusRes = await axios.get(`${API}/contracts/email-jobs/${jobId}`);
          const s = statusRes?.data?.status;
          if (s === "sent") {
            finalStatus = "sent";
            finalLog = statusRes?.data?.log;
            break;
          }
          if (s === "failed") {
            finalStatus = "failed";
            finalError = statusRes?.data?.error || "Eroare necunoscută";
            break;
          }
        } catch (pollErr) { /* ignoră erori tranzitorii */ }
      }
      if (finalStatus === "sent") {
        const shortLink = finalLog?.portal_short_link;
        const count = finalLog?.attachments_count || 0;
        toast.success(
          `Portal client actualizat! ${count} fişier${count > 1 ? "e" : ""} urcat${count > 1 ? "e" : ""}. Email cu link a fost trimis.`,
          { id: loadingId, duration: 8000 }
        );
        if (shortLink && navigator.clipboard) {
          try {
            await navigator.clipboard.writeText(shortLink);
            setTimeout(() => toast.info(`Link portal copiat: ${shortLink}`, { duration: 6000 }), 500);
          } catch (e) { /* silent */ }
        }
        if (onSent) onSent();
        onOpenChange(false);
      } else if (finalStatus === "failed") {
        toast.error(`Trimiterea prin portal a eșuat: ${finalError}`, { id: loadingId, duration: 10000 });
      } else {
        toast.warning("Procesarea continuă în background. Verifică Jobs în procesare.", { id: loadingId, duration: 8000 });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Eroare la trimitere prin portal", { id: loadingId });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !sending && onOpenChange(o)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-teal-700">
            <Mail className="w-5 h-5" />
            Trimite contract pe email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="ctr-recipient">Email destinatar *</Label>
            <Input
              id="ctr-recipient"
              type="email"
              placeholder="client@exemplu.ro"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              disabled={sending}
              data-testid="contract-email-recipient"
            />
          </div>

          <div>
            <Label htmlFor="ctr-recipient-name">Nume destinatar (opțional)</Label>
            <Input
              id="ctr-recipient-name"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              disabled={sending}
              data-testid="contract-email-recipient-name"
            />
          </div>

          <div>
            <Label htmlFor="ctr-subject">Subiect</Label>
            <Input
              id="ctr-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              data-testid="contract-email-subject"
            />
          </div>

          <div>
            <Label htmlFor="ctr-message">Mesaj (opțional)</Label>
            <Textarea
              id="ctr-message"
              rows={4}
              placeholder="Mesaj personalizat care apare în corpul emailului..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending}
              data-testid="contract-email-message"
            />
          </div>

          {/* Include deviz checkbox */}
          {devizData && (
            <label className="flex items-start gap-2 text-sm cursor-pointer p-3 rounded-lg border border-teal-100 bg-teal-50/40 hover:bg-teal-50">
              <input
                type="checkbox"
                checked={includeDeviz}
                onChange={(e) => setIncludeDeviz(e.target.checked)}
                disabled={sending}
                className="mt-0.5"
                data-testid="contract-include-deviz"
              />
              <span>
                <span className="flex items-center gap-1 font-semibold text-teal-800">
                  <Receipt className="w-4 h-4" />
                  Trimite împreună cu devizul sursă
                </span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Deviz <strong>{devizData.numarDeviz || "—"}</strong> — atașăm și PDF-ul devizului
                  pe baza căruia s-a generat contractul.
                </span>
              </span>
            </label>
          )}

          {/* Anexe generate - checkbox list */}
          {annexes.length > 0 && (
            <div className="space-y-2 p-3 rounded-lg border border-blue-100 bg-blue-50/40">
              <div className="flex items-center gap-2 text-sm font-semibold text-blue-800">
                <ClipboardList className="w-4 h-4" />
                Anexe generate ({annexes.length})
              </div>
              <p className="text-xs text-slate-500">
                Bifează anexele pe care vrei să le atașezi — PDF-ul fiecăreia se generează automat.
              </p>
              <div className="space-y-1.5">
                {annexes.map((a) => (
                  <label
                    key={a.id}
                    className="flex items-center gap-2 text-sm cursor-pointer px-2 py-1.5 rounded hover:bg-blue-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAnnexIds.includes(a.id)}
                      onChange={() => toggleAnnex(a.id)}
                      disabled={sending}
                      data-testid={`contract-annex-check-${a.id}`}
                    />
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-800 text-white flex-shrink-0">
                      {a.annex_label}
                    </span>
                    <span className="truncate text-slate-700">{a.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 p-3 rounded-lg bg-teal-50 border border-teal-200 text-xs text-teal-800">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Documentele (contract + anexele bifate) se încarcă în <b>Portalul Clientului</b>, iar clientul
              primește un email cu link de acces pentru a le vizualiza și descărca. Nu se atașează fișiere PDF în email.
            </span>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t">
            <Button
              variant="outline"
              onClick={resetAndClose}
              disabled={sending}
              data-testid="contract-email-cancel"
            >
              Anulează
            </Button>
            <Button
              onClick={handleSendViaPortal}
              disabled={sending || !recipient.trim()}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="contract-send-via-portal"
              title="Documentele merg în Portalul Client, clientul primește email cu link pentru descărcare"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se procesează...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Trimite către client</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
